/**
 * Cross-app tenant selection sync via localStorage.
 *
 * Both portal-spa (Vue) and connectaiq (React) share the same origin
 * (via nginx reverse proxy) and use localStorage key CONNECTAIQ_STORAGE_KEY to
 * communicate the active tenant. This module handles reading, writing,
 * and reacting to changes in that shared state.
 *
 * Sync triggers:
 *  - App boot:        setSelectedEntityViewOnAppLoad() reads localStorage
 *  - Cross-tab:       "storage" event fires when another tab writes
 *  - Bfcache restore: "pageshow" event fires when the page is restored
 *                     from the back/forward cache
 *
 * Listeners are registered in app.js after the Vue app mounts.
 */

import { datadogLogs } from "@datadog/browser-logs";
import type { Store } from "vuex";

import { CONNECTAIQ_STORAGE_KEY, PERMISSIONS } from "@/constants";
import { getEntityById } from "@/services/utils";

interface RootState {
  currentUser?: {
    permissions?: string[];
  };
}

export type TenantContext = {
  tenantId?: string;
};

export type SharedAppStorage = {
  tenantContext?: TenantContext;
};

/** Reads the full tenant context object from shared localStorage. */
export const getStoredTenantContext = (): TenantContext | undefined => {
  try {
    const data: SharedAppStorage = JSON.parse(localStorage.getItem(CONNECTAIQ_STORAGE_KEY) || "{}");
    return data?.tenantContext;
  } catch (err) {
    datadogLogs.logger.error(
      "Failed to parse tenant context from localStorage",
      { storage_key: CONNECTAIQ_STORAGE_KEY },
      err as Error,
    );
  }
};

/** Reads the active tenant ID from shared localStorage. */
export const getStoredTenantContextId = (): string | undefined => {
  const tenantContext = getStoredTenantContext();
  return tenantContext?.tenantId || undefined;
};

/** Writes the active tenant ID to shared localStorage. */
export const setLocalStorageSelectedEntityId = (tenantId: string): void => {
  try {
    const tenantContext = getStoredTenantContext();
    const newTenantContext: SharedAppStorage = { tenantContext: { ...tenantContext, tenantId: tenantId } };
    localStorage.setItem(CONNECTAIQ_STORAGE_KEY, JSON.stringify(newTenantContext));
  } catch (err) {
    datadogLogs.logger.error(
      "Failed to write tenant ID to localStorage",
      { storage_key: CONNECTAIQ_STORAGE_KEY, tenant_id: tenantId },
      err as Error,
    );
  }
};

/**
 * Checks whether the current user is allowed to view data for the given tenant.
 *
 * Tenant Managers and Super Admins can access any entity — the entity selector
 * already enforces this by passing an empty filter to the API. All other users
 * are restricted to entities present in their JWT entity_ids list (or profile
 * colleges as a fallback).
 */
export const isAuthorizedTenant = (store: Store<RootState>, tenantId: string): boolean => {
  const permissions: string[] = store.state.currentUser?.permissions ?? [];
  if (permissions.includes(PERMISSIONS.TENANT_MANAGER) || permissions.includes(PERMISSIONS.SUPER_ADMIN)) {
    return true;
  }

  const userEntityIds: string[] = store.getters.getEntityIds || [];
  return userEntityIds.includes(tenantId);
};

/** Fetches full entity details by ID and commits them to the Vuex store. */
export const fetchAndCommitSelectedTenant = async (store: Store<RootState>, tenantId: string): Promise<void> => {
  const entity = await getEntityById(tenantId);
  if (entity) {
    store.commit("programManager/setSelectedEntityView", entity);
  }
};

/**
 * Reconciles a tenant ID from an external source (localStorage, storage event)
 * with the current Vuex selection. Skips the update if the tenant hasn't changed
 * or the user isn't authorized for it.
 */
export const syncSelectedEntityFromLocalStorage = async (
  store: Store<RootState>,
  newTenantId: string,
): Promise<void> => {
  const currentEntity = store.getters["programManager/getSelectedEntityView"];

  if (currentEntity?.id === newTenantId) {
    return;
  }

  if (isAuthorizedTenant(store, newTenantId)) {
    await fetchAndCommitSelectedTenant(store, newTenantId);
  }
};

/**
 * Handles the "storage" event, which fires when another tab (or the connectaiq
 * React app in a different tab) writes to localStorage. Only reacts to changes
 * on the CONNECTAIQ_STORAGE_KEY key.
 */
export const handleStorageEvent =
  (store: Store<RootState>) =>
  async (event: StorageEvent): Promise<void> => {
    if (event.key !== CONNECTAIQ_STORAGE_KEY) {
      return;
    }

    try {
      const data: SharedAppStorage = JSON.parse(event.newValue || "{}");
      const tenantId = data?.tenantContext?.tenantId;
      if (tenantId) {
        await syncSelectedEntityFromLocalStorage(store, tenantId);
      }
    } catch (err) {
      datadogLogs.logger.error("Failed to handle cross-tab tenant storage event", {
        storage_key: CONNECTAIQ_STORAGE_KEY,
      });
    }
  };

/**
 * Handles the "pageshow" event. Only acts when event.persisted is true,
 * meaning the page was restored from the back/forward cache. In that case
 * the Vuex store is stale and localStorage may hold a newer tenant selection.
 */
export const handlePageShow =
  (store: Store<RootState>) =>
  async (event: PageTransitionEvent): Promise<void> => {
    if (!event.persisted) {
      return;
    }

    const tenantId = getStoredTenantContextId();
    if (tenantId) {
      await syncSelectedEntityFromLocalStorage(store, tenantId);
    }
  };
